import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import moment from 'moment';

import { useUserDetailsContext } from 'context/userDetailsContext';
import LabelName from 'components/UI/LabelName/LabelName';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import TimezoneDatePickerWithTime from 'components/TimezoneDatePickerWithTime/TimezoneDatePickerWithTime';

function EventDate({ control, errors, setValue, watch }) {
  const { userDetails } = useUserDetailsContext();
  const allDay = watch('all_day');
  const whenValue = watch('when');
  const tillValue = watch('till');

  // Keep Till date same as When & clear it when All Day is turned off
  useEffect(() => {
    if (!allDay) {
      setValue('till', null);
    } else if (!watch('till')) {
      setValue('till', whenValue);
    }
  }, [allDay, setValue, watch, whenValue]);

  return (
    <div className="d-flex align-items-center py-2 flex-wrap">
      {/* Label */}
      <div className="col-md-4 col-12">
        <LabelName required={true} htmlFor="when">
          Date
        </LabelName>
      </div>

      {/* Fields */}
      <div className="col-md-8 col-12 d-flex justify-content-between align-items-start">
        {/* Start Date ("When") */}
        <div className={allDay ? 'w-50 pe-2' : 'w-100'}>
          <Controller
            name="when"
            control={control}
            rules={{ required: 'Please select event date' }}
            render={({ field }) => (
              <TimezoneDatePickerWithTime
                id={field.name}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.value);

                  // If all-day event and new "when" date is greater than "till", update "till"
                  if (allDay && tillValue && e.value) {
                    const newWhenDate = moment(e.value).startOf('day');
                    const currentTillDate = moment(tillValue).startOf('day');

                    if (newWhenDate.isAfter(currentTillDate)) {
                      setValue('till', e.value);
                    }
                  }
                }}
                // minDate={new Date()}
                placeholder="When"
                monthNavigator
                yearNavigator
                yearRange={`${new Date().getFullYear()}:${new Date().getFullYear() + 100}`}
                className="input-shadow w-100"
                inputRef={field.ref}
                timezone={userDetails?.timezone}
              />
            )}
          />
          {errors?.when && <InputErrorMessage className="mt-1">{errors.when.message}</InputErrorMessage>}
        </div>

        {/* End Date ("Till") – only if All Day checked */}
        {allDay && (
          <div className="w-50">
            <Controller
              name="till"
              control={control}
              rules={{
                required: allDay ? 'Please select end date' : false,
                validate: (value) => {
                  if (!value || !whenValue) return true;

                  // Compare only dates, not times
                  const tillDate = moment(value).startOf('day');
                  const whenDate = moment(whenValue).startOf('day');

                  if (tillDate.isBefore(whenDate)) {
                    return 'End date cannot be earlier than start date';
                  }

                  return true;
                },
              }}
              render={({ field }) => (
                <TimezoneDatePickerWithTime
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  minDate={whenValue || new Date()}
                  placeholder="Till"
                  monthNavigator
                  yearNavigator
                  yearRange={`${new Date().getFullYear()}:${new Date().getFullYear() + 100}`}
                  className="input-shadow w-100"
                  inputRef={field.ref}
                  timezone={userDetails?.timezone}
                />
              )}
            />
            {allDay && errors?.till && <InputErrorMessage className="mt-1">{errors.till.message}</InputErrorMessage>}
          </div>
        )}
      </div>
    </div>
  );
}

export default EventDate;
